import type { SkillGlyph } from '@/components/skilltree/svg/glyph';
import {
  HomeGlyph, PersonalityGlyph, DomainsGlyph, CardsGlyph, ReportsGlyph, HistoryGlyph,
  AccountGlyph, AssessGlyph, IntegralGlyph, QuizzesGlyph, IQGlyph, ReflectGlyph,
  RelationshipsGlyph, BookmarksGlyph, LikerGlyph, MentorGlyph, CareerGlyph,
  AstrologyGlyph, CommunityGlyph, WalletGlyph, ShopGlyph, ScienceGlyph,
  AdminGlyph, PeopleGlyph, ReportCardGlyph,
} from '@/components/nav/icons';
import { DISCORD_INVITE_URL } from '@/config/community';

export type NavigationGroup = 'You' | 'Assess' | 'Connect' | 'Coins' | 'Learn' | 'System';
export interface NavigationContext {
  isAdmin: boolean;
  hasTPSAssessment: boolean;
  astrologyEnabled: boolean;
}
export interface NavigationItem {
  id: string;
  path: string;
  label: string;
  shortLabel?: string;
  group: NavigationGroup;
  icon: SkillGlyph;
  aliases?: string[];
  external?: boolean;
  primaryMobile?: boolean;
  keywords?: string[];
  visible?: (context: NavigationContext) => boolean;
}

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { id: 'home', path: '/', label: 'Home', group: 'You', icon: HomeGlyph, primaryMobile: true },
  { id: 'personality', path: '/personality', label: 'My Personality', shortLabel: 'Personality', group: 'You', icon: PersonalityGlyph, primaryMobile: true, aliases: ['/dashboard/framework'], keywords: ['profile', 'frameworks', 'typing'] },
  { id: 'tps-profile', path: '/tps-profile', label: 'TPS Domains', shortLabel: 'TPS', group: 'You', icon: DomainsGlyph, visible: ({ hasTPSAssessment }) => hasTPSAssessment },
  { id: 'cards', path: '/cards', label: 'Trait Cards', shortLabel: 'Cards', group: 'You', icon: CardsGlyph, visible: ({ hasTPSAssessment }) => hasTPSAssessment },
  { id: 'reports', path: '/reports', label: 'Reports', group: 'You', icon: ReportsGlyph, keywords: ['pdf', 'deep dive'] },
  { id: 'history', path: '/history', label: 'History', group: 'You', icon: HistoryGlyph },
  { id: 'account', path: '/profile?tab=profile', label: 'Account profile', shortLabel: 'Account', group: 'You', icon: AccountGlyph, aliases: ['/profile'] },

  { id: 'assessment', path: '/tps-assessment-hub', label: 'Take Assessment', shortLabel: 'Assess', group: 'Assess', icon: AssessGlyph, aliases: ['/tps-onboarding', '/assessment'] },
  { id: 'integral', path: '/integral', label: 'Integral', group: 'Assess', icon: IntegralGlyph, aliases: ['/skill-tree', '/integral-assessment'] },
  { id: 'quizzes', path: '/quizzes', label: 'Quizzes', group: 'Assess', icon: QuizzesGlyph, aliases: ['/quiz'] },
  { id: 'iq', path: '/iq-test', label: 'IQ Test', group: 'Assess', icon: IQGlyph },
  { id: 'cognitive-report', path: '/cognitive-report', label: 'Cognitive Report Card', shortLabel: 'Report Card', group: 'Assess', icon: ReportCardGlyph, keywords: ['iq', 'grades', 'cognitive', 'intelligence'] },
  { id: 'reflect', path: '/reflect', label: 'Daily Reflection', shortLabel: 'Reflect', group: 'Assess', icon: ReflectGlyph },

  { id: 'social', path: '/social', label: 'Social', group: 'Connect', icon: PeopleGlyph, aliases: ['/social/boards', '/social/mail', '/social/members'], visible: ({ isAdmin }) => isAdmin },
  { id: 'relationships', path: '/relationships', label: 'Relationships', shortLabel: 'Relations', group: 'Connect', icon: RelationshipsGlyph, aliases: ['/relationship'] },
  { id: 'bookmarks', path: '/bookmarks', label: 'Bookmarks', shortLabel: 'Saved', group: 'Connect', icon: BookmarksGlyph },
  { id: 'liker', path: '/liker', label: 'Liker', group: 'Connect', icon: LikerGlyph, primaryMobile: true, aliases: ['/liker-session'] },
  { id: 'mentor', path: '/mentor', label: 'AI Mentor', group: 'Connect', icon: MentorGlyph, aliases: ['/mentor-arc'] },
  { id: 'career', path: '/career', label: 'Career', group: 'Connect', icon: CareerGlyph },
  { id: 'astrology', path: '/astrology', label: 'Astrology', group: 'Connect', icon: AstrologyGlyph, visible: ({ astrologyEnabled }) => astrologyEnabled },
  { id: 'discord', path: DISCORD_INVITE_URL, label: 'Discord', group: 'Connect', icon: CommunityGlyph, external: true },

  { id: 'wallet', path: '/earn', label: 'Wallet & Earn', shortLabel: 'Wallet', group: 'Coins', icon: WalletGlyph },
  { id: 'shop', path: '/shop', label: 'Coin Shop', shortLabel: 'Shop', group: 'Coins', icon: ShopGlyph },
  { id: 'science', path: '/science', label: 'Science', group: 'Learn', icon: ScienceGlyph },
  { id: 'admin', path: '/admin', label: 'Admin', group: 'System', icon: AdminGlyph, visible: ({ isAdmin }) => isAdmin },
];

export const NAVIGATION_GROUPS: NavigationGroup[] = ['You', 'Assess', 'Connect', 'Coins', 'Learn', 'System'];

export function visibleNavigationItems(context: NavigationContext): NavigationItem[] {
  return NAVIGATION_ITEMS.filter((item) => !item.visible || item.visible(context));
}

function cleanPath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '') || '/';
}

export function navigationItemIsActive(item: NavigationItem, pathname: string): boolean {
  if (item.external) return false;
  const current = cleanPath(pathname);
  const candidates = [item.path, ...(item.aliases ?? [])].map(cleanPath);
  return candidates.some((candidate) => candidate === '/' ? current === '/' : current === candidate || current.startsWith(`${candidate}/`));
}

export function navigationItemForPath(pathname: string, items: NavigationItem[] = NAVIGATION_ITEMS): NavigationItem | undefined {
  return items.find((item) => navigationItemIsActive(item, pathname));
}
